
"use client";

import { Menu, Transition } from "@headlessui/react";
import { Fragment, useSyncExternalStore } from "react";
import { ChevronDown } from "lucide-react";

export interface MenuItem {
    label: string;
    onClick: () => void;
    icon?: React.ComponentType<{ className?: string }>;
    disabled?: boolean;
    className?: string;
}

interface MenuDropdownProps {
    label: string;
    items: MenuItem[];
    icon?: React.ComponentType<{ className?: string }>;
    variant?: "primary" | "secondary" | "outline" | "ghost";
    align?: "left" | "right";
}

export function MenuDropdown({
    label,
    items,
    icon: Icon,
    variant = "primary",
    align = "right"
}: MenuDropdownProps) {
    const isHydrated = useSyncExternalStore(
        () => () => {},
        () => true,
        () => false,
    );

    const getButtonClasses = () => {
        const baseClasses = "inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold transition-all shadow-sm";
        switch (variant) {
            case "primary":
                return `${baseClasses} bg-indigo-600 text-white hover:bg-indigo-700 shadow-indigo-200`;
            case "secondary":
                return `${baseClasses} bg-fuchsia-600 text-white hover:bg-fuchsia-500 shadow-fuchsia-900/20`;
            case "outline":
                return `${baseClasses} border border-slate-300 bg-white text-slate-700 hover:bg-slate-50`;
            case "ghost":
                return `${baseClasses} text-slate-500 hover:text-slate-900 hover:bg-slate-100 shadow-none`;
            default:
                return baseClasses;
        }
    };

    if (!isHydrated) {
        return (
            <div className="relative inline-block text-left">
                <button type="button" className={getButtonClasses()}>
                    {Icon && <Icon className="h-4 w-4" />}
                    {label}
                    <ChevronDown className="ml-1 h-4 w-4" aria-hidden="true" />
                </button>
            </div>
        );
    }

    return (
        <Menu as="div" className="relative inline-block text-left">
            <div>
                <Menu.Button className={getButtonClasses()}>
                    {Icon && <Icon className="h-4 w-4" />}
                    {label}
                    <ChevronDown className="ml-1 h-4 w-4" aria-hidden="true" />
                </Menu.Button>
            </div>
            <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
            >
                <Menu.Items className={`absolute z-50 mt-2 w-56 rounded-xl bg-white shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none ${align === "right" ? "right-0" : "left-0"} p-1`}>
                    <div className="px-1 py-1">
                        {items.map((item, index) => (
                            <Menu.Item key={index} disabled={item.disabled}>
                                {({ active }) => (
                                    <button
                                        className={`${active ? "bg-indigo-50 text-indigo-900" : "text-slate-700"
                                            } group flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${item.disabled ? 'opacity-50 cursor-not-allowed' : ''} ${item.className || ''}`}
                                        onClick={item.onClick}
                                        disabled={item.disabled}
                                    >
                                        {item.icon && (
                                            <item.icon className={`h-4 w-4 ${active ? 'text-indigo-600' : 'text-slate-400'}`} />
                                        )}
                                        {item.label}
                                    </button>
                                )}
                            </Menu.Item>
                        ))}
                    </div>
                </Menu.Items>
            </Transition>
        </Menu>
    );
}
